import type { LayerSpecification } from 'mapbox-gl';
import { CONSUMPTION_CLASSES, closeRing } from './domain';
import { PC } from './tokens';
import type { HeatNetwork, LngLat, PlanBuilding, Referential } from './types';

type FC = GeoJSON.FeatureCollection;
export const EMPTY: FC = { type: 'FeatureCollection', features: [] };

export function buildingsFC(buildings: PlanBuilding[], props: (b: PlanBuilding) => Record<string, number | string | null>): FC {
  return {
    type: 'FeatureCollection',
    features: buildings.map((b) => ({ type: 'Feature', id: b.num, properties: { id: b.num, fid: b.id, ...props(b) }, geometry: b.geometry })),
  };
}

export function ringFC(rings: LngLat[][]): FC {
  return {
    type: 'FeatureCollection',
    features: rings.filter((r) => r.length >= 3).map((r, i) => ({
      type: 'Feature', id: i + 1, properties: { id: i + 1 }, geometry: { type: 'Polygon', coordinates: [closeRing(r)] },
    })),
  };
}

export function communesFC(codes: string[], geometries: Referential['communeGeometries']): FC {
  return {
    type: 'FeatureCollection',
    features: codes.filter((code) => geometries[code]).map((code) => ({
      type: 'Feature', id: code, properties: { code }, geometry: geometries[code],
    })),
  };
}

/** Tracés complets : la sélection du projet ne masque pas les autres réseaux locaux. */
export function networksFC(networks: HeatNetwork[], selected?: Set<string>): FC {
  return {
    type: 'FeatureCollection',
    features: networks.filter((n) => n.geometry || n.path.length >= 2).map((n, i) => ({
      type: 'Feature', id: i + 1,
      properties: { id: n.id, name: n.name, code: n.code, isEngie: n.isEngie ?? null, selected: !selected || selected.has(n.id) ? 1 : 0 },
      geometry: n.geometry ?? { type: 'LineString', coordinates: n.path },
    })),
  };
}

/** Polygone dessiné : surface + contour + sommets. */
export function drawFC(points: LngLat[], closed: boolean, cursor?: LngLat): FC {
  const features: GeoJSON.Feature[] = [];
  if (points.length >= 3) {
    features.push({ type: 'Feature', id: 1001, properties: { id: 1001 }, geometry: { type: 'Polygon', coordinates: [closeRing(points)] } });
  }
  const line = closed ? closeRing(points) : cursor && points.length ? [...points, cursor] : points;
  if (line.length >= 2) {
    features.push({ type: 'Feature', id: 1000, properties: { id: 1000 }, geometry: { type: 'LineString', coordinates: line } });
  }
  points.forEach((p, i) => features.push({ type: 'Feature', id: i + 2000, properties: { id: i + 2000, vertex: i, first: i === 0 ? 1 : 0 }, geometry: { type: 'Point', coordinates: p } }));
  if (closed) points.forEach((p, i) => {
    const next = points[(i + 1) % points.length];
    features.push({ type: 'Feature', id: i + 10000, properties: { midpoint: i }, geometry: { type: 'Point', coordinates: [(p[0] + next[0]) / 2, (p[1] + next[1]) / 2] } });
  });
  return { type: 'FeatureCollection', features };
}

/** Stations météo : zone d'influence (polygone) + point de mesure. */
export function stationsFC(
  stations: { id: string; name: string; position: LngLat; area: LngLat[]; hidePoint?: boolean; serviceAreas?: (GeoJSON.Polygon | GeoJSON.MultiPolygon)[] }[],
  selectedId: string | null,
): FC {
  const features: GeoJSON.Feature[] = [];
  stations.forEach((s, i) => {
    const sel = s.id === selectedId ? 1 : 0;
    for (const geometry of s.serviceAreas ?? []) {
      features.push({ type: 'Feature', properties: { id: s.id, name: s.name, sel }, geometry });
    }
    if (s.area.length >= 3) features.push({
      type: 'Feature', id: 100 + i, properties: { id: s.id, name: s.name, sel },
      geometry: { type: 'Polygon', coordinates: [closeRing(s.area)] },
    });
    if (!s.hidePoint) features.push({
      type: 'Feature', id: 200 + i, properties: { id: s.id, name: s.name, sel },
      geometry: { type: 'Point', coordinates: s.position },
    });
  });
  return { type: 'FeatureCollection', features };
}

/** Couches des stations météo, superposées à la carte de l'assistant. */
export function stationLayers(): LayerSpecification[] {
  return [
    { id: 'pc-station-zone', type: 'fill', source: 'stations', filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'fill-color': ['case', ['==', ['get', 'sel'], 1], PC.station, PC.stationIdle] as never, 'fill-opacity': ['case', ['==', ['get', 'sel'], 1], 0.09, 0.025] as never } },
    { id: 'pc-station-zone-line', type: 'line', source: 'stations', filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'line-color': ['case', ['==', ['get', 'sel'], 1], PC.station, PC.stationIdle] as never, 'line-width': ['case', ['==', ['get', 'sel'], 1], 3, 1.5] as never, 'line-dasharray': [1, 2] } },
    { id: 'pc-station-point', type: 'circle', source: 'stations', filter: ['==', ['geometry-type'], 'Point'], paint: { 'circle-radius': ['case', ['==', ['get', 'sel'], 1], 9, 6] as never, 'circle-color': ['case', ['==', ['get', 'sel'], 1], PC.station, '#ffffff'] as never, 'circle-stroke-color': PC.station, 'circle-stroke-width': 2 } },
  ];
}

  /** Carte de l'assistant : bâtiments UM inclus / exclus, contours, réseaux, outil de dessin. */
export function perimeterLayers(): LayerSpecification[] {
  return [
    { id: 'pc-wiz-building', type: 'fill', source: 'buildings', paint: { 'fill-color': ['case', ['==', ['get', 'inc'], 1], PC.included, PC.excluded] as never, 'fill-opacity': ['case', ['==', ['get', 'inc'], 1], 0.75, 0.45] as never } },
    { id: 'pc-wiz-building-included', type: 'line', source: 'buildings', filter: ['==', ['get', 'inc'], 1], paint: { 'line-color': PC.included, 'line-width': 1.5 } },
    { id: 'pc-wiz-building-excluded', type: 'line', source: 'buildings', filter: ['!=', ['get', 'inc'], 1], paint: { 'line-color': PC.excluded, 'line-width': 1.5, 'line-dasharray': [2, 2] } },
    { id: 'pc-wiz-outline-fill', type: 'fill', source: 'outline', paint: { 'fill-color': PC.outline, 'fill-opacity': 0.06 } },
    { id: 'pc-wiz-outline', type: 'line', source: 'outline', paint: { 'line-color': PC.outline, 'line-width': 2, 'line-dasharray': [2, 1.5] } },
    { id: 'pc-wiz-network-halo', type: 'line', source: 'networks', paint: { 'line-color': '#ffffff', 'line-width': 7, 'line-opacity': 0.9 } },
    { id: 'pc-wiz-network', type: 'line', source: 'networks', filter: ['==', ['get', 'isEngie'], true], paint: {
      'line-color': ['case', ['==', ['get', 'isEngie'], true], PC.networkEngie, ['==', ['get', 'isEngie'], false], PC.networkNonEngie, PC.excluded],
      'line-width': ['case', ['==', ['get', 'selected'], 1], 5, 2.5],
      'line-opacity': ['case', ['==', ['get', 'selected'], 1], 1, 0.65],
    } },
    { id: 'pc-wiz-network-other', type: 'line', source: 'networks', filter: ['!=', ['get', 'isEngie'], true], paint: {
      'line-color': PC.networkNonEngie,
      'line-width': ['case', ['==', ['get', 'selected'], 1], 5, 2.5],
      'line-opacity': ['case', ['==', ['get', 'selected'], 1], 1, 0.65],
    } },
    { id: 'pc-draw-fill', type: 'fill', source: 'draw', filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'fill-color': PC.draw, 'fill-opacity': 0.14 } },
    { id: 'pc-draw-line', type: 'line', source: 'draw', filter: ['==', ['geometry-type'], 'LineString'], paint: { 'line-color': PC.draw, 'line-width': 2.5, 'line-dasharray': [2, 1.5] } },
    {
      id: 'pc-draw-vertex', type: 'circle', source: 'draw', filter: ['has', 'vertex'],
      paint: { 'circle-radius': ['case', ['==', ['get', 'first'], 1], 6, 4] as never, 'circle-color': '#ffffff', 'circle-stroke-color': PC.draw, 'circle-stroke-width': 2 },
    },
    { id: 'pc-draw-midpoint', type: 'circle', source: 'draw', filter: ['has', 'midpoint'],
      paint: { 'circle-radius': 4, 'circle-color': PC.draw, 'circle-opacity': 0.6, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1 } },
  ];
}

const consoColor = (() => {
  const expr: unknown[] = ['step', ['get', 'conso'], CONSUMPTION_CLASSES[0].color];
  CONSUMPTION_CLASSES.slice(0, -1).forEach((c, i) => expr.push(c.max, CONSUMPTION_CLASSES[i + 1].color));
  return expr;
})();

/** Carte du projet : bâtiments colorés par conso corrigée, contour du périmètre, bâtiment sélectionné. */
export function projectLayers(pickedNum: number | null, planActive = false): LayerSpecification[] {
  const fade = planActive ? ['case', ['==', ['get', 'plan'], 1], 0.95, 0.18] : 0.9;
  return [
    { id: 'pc-prj-building', type: 'fill', source: 'buildings', paint: { 'fill-color': ['case', ['==', ['get', 'conso'], null], PC.excluded, consoColor] as never, 'fill-opacity': fade as never } },
    { id: 'pc-prj-building-line', type: 'line', source: 'buildings', paint: { 'line-color': '#ffffff', 'line-width': 0.4, 'line-opacity': 0.6 } },
    { id: 'pc-prj-outline', type: 'line', source: 'outline', paint: { 'line-color': PC.outline, 'line-width': 2.5, 'line-dasharray': [2, 1.5] } },
    ...(planActive ? planLayers() : []),
    { id: 'pc-prj-picked', type: 'line', source: 'buildings', filter: ['==', ['get', 'id'], pickedNum ?? -1], paint: { 'line-color': '#6ED2B1', 'line-width': 3.5 } },
  ];
}

/** Plan de développement choisi : bâtiments raccordés cerclés, tracé du réseau par-dessus. */
function planLayers(): LayerSpecification[] {
  return [
    { id: 'pc-plan-building', type: 'line', source: 'buildings', filter: ['==', ['get', 'plan'], 1], paint: { 'line-color': PC.draw, 'line-width': 1.6 } },
    { id: 'pc-plan-route-halo', type: 'line', source: 'plan-route', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#ffffff', 'line-width': 6, 'line-opacity': 0.9 } },
    { id: 'pc-plan-route', type: 'line', source: 'plan-route', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': PC.draw, 'line-width': 3 } },
  ];
}
